/**
 * Isolierte Solo-Simulation-Sandbox.
 * STRIKT: Kein Schreibzugriff auf clientData / appStore / Firestore.
 */
import { create } from 'zustand';

export const SOLO_ORANGE_BWA_KEYS = [
  /** Versicherungen/Beiträge: umsatzgekoppelt (Solo-Einsparung über Umsatzfaktor) */
  'versicherungen',
  'werbeReisekosten',
  'reparatur',
  'sonstigeKosten',
] as const;

export const SOLO_PRODUCTIVE_MONTHS = 10;
export const SOLO_YEAR_MONTHS = 12;
export const SOLO_BUFFER_FACTOR = SOLO_YEAR_MONTHS / SOLO_PRODUCTIVE_MONTHS; // 1.2
export const SOLO_GAIN_FACTOR = 1.1;
export const SOLO_DEFAULT_VAT = 1.19;
export const SOLO_DEFAULT_UMSATZ_BRUTTO = 9_500;
export const SOLO_DEFAULT_WOCHENSTUNDEN = 38;

/** Read-Only Basis aus Live-Akte (nie zurückschreiben) */
export interface SoloCostLine {
  key: string;
  label: string;
  /** Monatlicher Netto-Betrag aus BWA / Zeitraum */
  monatNetto: number;
}

export interface SoloSimulationSnapshot {
  sourceMandantUid: string | null;
  sourceYear: string | null;
  capturedAt: string;
  baseFixkostenNetto: number;
  baseVariableKostenQuotePercent: number;
  baseMaterialQuotePercent: number;
  /**
   * Geräte: Fixbetrag Ø-Monat aus Live-BWA (nicht umsatzgekoppelt).
   * Toggle = komplett rein oder komplett raus.
   */
  baseGeraetekostenMonat: number;
  /** Read-Only Gerätefaktor Live-Akte (Anzeige „Vorher“) */
  baseGeraetekostenfaktor: number;
  baseSteuersatzPercent: number;
  baseGewerbesteuerBetrag: number;
  /** Read-Only Ist-Personalkosten (MA) — Solo-Sim tilgt diese auf 0 € */
  basePersonalkostenIst: number;
  /** Einzelzeilen Fixkosten (Whitelist) für Detailansicht */
  fixCostLines: SoloCostLine[];
  /** Einzelzeilen variable Orange-Kosten für Detailansicht */
  variableCostLines: SoloCostLine[];
  historischerMonatsUmsatzNetto: number;
  mwstFaktor: number;
  gewerbesteuerInBetrieblichenSteuern: boolean;
  /** Prefill-Hilfen (Read-Only) */
  defaultMonatsUmsatzBrutto: number;
  defaultUnternehmerlohn: number;
  defaultWochenstunden: number;
  lohnfortzahlungPercent: number;
  verlustPercent: number;
  warenwirtschaftAufschlagVk: number;
  liquiditaetPercent: number;
}

/** Virtuelle Stellschrauben (nur Sandbox) */
export interface SoloSimulationKnobs {
  monatsUmsatzBrutto: number;
  /** Geplanter Unternehmerlohn (Basis; Aufwand = × (1+EK%)) */
  unternehmerLohnZiel: number;
  wochenstunden: number;
  /**
   * Bipolar: negativ = Einsparung (von Fixkosten abgezogen),
   * positiv = Zusatzkosten (zu Fixkosten addiert).
   */
  gemeinkostenAnpassungNetto: number;
  ekSteuersatzPercent: number;
  azubiAktiv: boolean;
  azubiVerguetungBrutto: number;
  azubiWochenstunden: number;
  /** false = Geräte komplett aus Solo-Kalkulation (Fixbetrag, kein Anteil) */
  geraeteAktiv: boolean;
}

export interface SoloSimulationDerived {
  monatsUmsatzNetto: number;
  azubiKostenMonat: number;
  /** Fix + Anpassung + variabel + Azubi (OHNE Unternehmerlohn, OHNE Material) */
  virtuelleGemeinkosten: number;
  virtuellerMaterialaufwand: number;
  unternehmerAufwand: number;
  /**
   * Ø Gesamtkosten p. Monat (netto):
   * Fix + Anpassung + variabel + Unternehmeraufwand (+ Azubi) + Geräte (wenn aktiv).
   * KEIN Material (Material bleibt Faktor/Kalkulator-seitig).
   */
  gesamtKostenSolo: number;
  /** Geräteaufwand p. Monat (0 wenn geraeteAktiv=false) */
  virtuellerGeraeteaufwand: number;
  virtuellerGemeinkostenfaktor: number;
  /** Unternehmeraufwand / produktive Monatsstunden */
  prodStundenlohnChef: number;
  /** Solo: prod. Lohnanteil = Unternehmeraufwand / Umsatz */
  prodLohnkostenfaktor: number;
  materialkostenfaktor: number;
  /** Gerätefaktor (0 wenn ausgeschaltet) */
  geraetekostenfaktor: number;
  ueberschussNetto: number;
  ueberschussProzent: number;
  inhaberErtragSoll: number;
  rentabilitaetSoll: number;
  /** Paar 1: ohne Urlaubspuffer */
  kostendeckungOhneUrlaubNetto: number;
  kostendeckungOhneUrlaubBrutto: number;
  sollOhneUrlaubNetto: number;
  sollOhneUrlaubBrutto: number;
  /** Paar 2: inkl. 10-Monate-Puffer (×1,2) */
  kostendeckungInklUrlaubNetto: number;
  kostendeckungInklUrlaubBrutto: number;
  sollInklUrlaubNetto: number;
  sollInklUrlaubBrutto: number;
}

export interface SoloSimulationState extends SoloSimulationKnobs {
  snapshot: SoloSimulationSnapshot | null;
  isHydrated: boolean;
  knobsSeeded: boolean;

  hydrateFromLiveAkte: (payload: {
    mandantUid: string;
    year: string;
    snapshot: Omit<SoloSimulationSnapshot, 'sourceMandantUid' | 'sourceYear' | 'capturedAt'> &
      Partial<Pick<SoloSimulationSnapshot, 'mwstFaktor' | 'gewerbesteuerInBetrieblichenSteuern'>>;
  }) => void;

  resetKnobs: () => void;
  clearSandbox: () => void;

  setMonatsUmsatzBrutto: (v: number) => void;
  setUnternehmerLohnZiel: (v: number) => void;
  setWochenstunden: (v: number) => void;
  setGemeinkostenAnpassungNetto: (v: number) => void;
  setEkSteuersatzPercent: (v: number) => void;
  setAzubiAktiv: (v: boolean) => void;
  setAzubiVerguetungBrutto: (v: number) => void;
  setAzubiWochenstunden: (v: number) => void;
  setGeraeteAktiv: (v: boolean) => void;

  getDerived: () => SoloSimulationDerived;
}

export const defaultSoloKnobs = (): SoloSimulationKnobs => ({
  monatsUmsatzBrutto: SOLO_DEFAULT_UMSATZ_BRUTTO,
  unternehmerLohnZiel: 2_500,
  wochenstunden: SOLO_DEFAULT_WOCHENSTUNDEN,
  gemeinkostenAnpassungNetto: 0,
  ekSteuersatzPercent: 0,
  azubiAktiv: false,
  azubiVerguetungBrutto: 0,
  azubiWochenstunden: 0,
  geraeteAktiv: true,
});

const emptyDerived = (): SoloSimulationDerived => ({
  monatsUmsatzNetto: 0,
  azubiKostenMonat: 0,
  virtuelleGemeinkosten: 0,
  virtuellerMaterialaufwand: 0,
  unternehmerAufwand: 0,
  gesamtKostenSolo: 0,
  virtuellerGeraeteaufwand: 0,
  virtuellerGemeinkostenfaktor: 0,
  prodStundenlohnChef: 0,
  prodLohnkostenfaktor: 0,
  materialkostenfaktor: 0,
  geraetekostenfaktor: 0,
  ueberschussNetto: 0,
  ueberschussProzent: 0,
  inhaberErtragSoll: 0,
  rentabilitaetSoll: 0,
  kostendeckungOhneUrlaubNetto: 0,
  kostendeckungOhneUrlaubBrutto: 0,
  sollOhneUrlaubNetto: 0,
  sollOhneUrlaubBrutto: 0,
  kostendeckungInklUrlaubNetto: 0,
  kostendeckungInklUrlaubBrutto: 0,
  sollInklUrlaubNetto: 0,
  sollInklUrlaubBrutto: 0,
});

export function computeSoloDerived(
  snap: SoloSimulationSnapshot | null,
  knobs: SoloSimulationKnobs
): SoloSimulationDerived {
  if (!snap) return emptyDerived();

  const vat = snap.mwstFaktor > 0 ? snap.mwstFaktor : SOLO_DEFAULT_VAT;
  const monatsUmsatzNetto = knobs.monatsUmsatzBrutto / vat;

  const azubiKostenMonat = knobs.azubiAktiv
    ? Math.max(0, knobs.azubiVerguetungBrutto)
    : 0;

  const anpassung = Number.isFinite(knobs.gemeinkostenAnpassungNetto)
    ? knobs.gemeinkostenAnpassungNetto
    : 0;

  const variableAnteil =
    monatsUmsatzNetto * (snap.baseVariableKostenQuotePercent / 100);

  // Fix + bipolar Anpassung + variabel + Azubi (kein UL, kein Material)
  const virtuelleGemeinkosten =
    snap.baseFixkostenNetto + anpassung + variableAnteil + azubiKostenMonat;

  const materialkostenfaktor = Math.max(0, snap.baseMaterialQuotePercent) / 100;
  const virtuellerMaterialaufwand = monatsUmsatzNetto * materialkostenfaktor;

  // Geräte = Fixbetrag (Live Ø-Monat): an = voll rein, aus = 0 — nie umsatzgekoppelt
  const virtuellerGeraeteaufwand = knobs.geraeteAktiv
    ? Math.max(0, snap.baseGeraetekostenMonat || 0)
    : 0;
  const geraetekostenfaktor =
    monatsUmsatzNetto > 0 ? virtuellerGeraeteaufwand / monatsUmsatzNetto : 0;

  const ek = Math.max(0, knobs.ekSteuersatzPercent);
  const unternehmerAufwand =
    Math.max(0, knobs.unternehmerLohnZiel) * (1 + ek / 100);

  // Gesamtkosten OHNE Material, MIT Geräte-Fixbetrag (wenn aktiv)
  const gesamtKostenSolo =
    virtuelleGemeinkosten + unternehmerAufwand + virtuellerGeraeteaufwand;

  const virtuellerGemeinkostenfaktor =
    monatsUmsatzNetto > 0 ? virtuelleGemeinkosten / monatsUmsatzNetto : 0;

  const prodLohnkostenfaktor =
    monatsUmsatzNetto > 0 ? unternehmerAufwand / monatsUmsatzNetto : 0;

  const stundenMonat = knobs.wochenstunden * (52 / 12);
  const prodStundenlohnChef =
    stundenMonat > 0 ? unternehmerAufwand / stundenMonat : 0;

  const ueberschussNetto = monatsUmsatzNetto - gesamtKostenSolo;
  const ueberschussProzent =
    monatsUmsatzNetto > 0 ? (ueberschussNetto / monatsUmsatzNetto) * 100 : 0;

  // Nur Rentabilitäts-Ebene (Kurzauswertung): Kalkulation (Aufwand/Soll) bleibt unverändert.
  // Unterdeckung → Minus frisst zuerst den Lohn; Steuer am auszahlbaren Lohn neu bemessen.
  const zielLohn = Math.max(0, knobs.unternehmerLohnZiel);
  let inhaberErtragSoll: number;
  if (ueberschussNetto >= 0) {
    inhaberErtragSoll = ueberschussNetto + unternehmerAufwand;
  } else {
    const auszahlbarerLohn = zielLohn + ueberschussNetto;
    if (auszahlbarerLohn >= 0) {
      const steuerEffektiv = auszahlbarerLohn * (ek / 100);
      inhaberErtragSoll = auszahlbarerLohn + steuerEffektiv;
    } else {
      inhaberErtragSoll = auszahlbarerLohn;
    }
  }
  const rentabilitaetSoll =
    monatsUmsatzNetto > 0 ? (inhaberErtragSoll / monatsUmsatzNetto) * 100 : 0;

  // Break-even: Geräte sind Fix → in den Zähler, nicht in die variable Quote
  const lastFixMonat =
    snap.baseFixkostenNetto +
    anpassung +
    azubiKostenMonat +
    unternehmerAufwand +
    virtuellerGeraeteaufwand;
  const q =
    (snap.baseVariableKostenQuotePercent + snap.baseMaterialQuotePercent) / 100;
  const denom = 1 - q;

  const kostendeckungOhneUrlaubNetto =
    denom > 0.01 ? lastFixMonat / denom : 0;
  const sollOhneUrlaubNetto = kostendeckungOhneUrlaubNetto * SOLO_GAIN_FACTOR;

  const kostendeckungInklUrlaubNetto =
    kostendeckungOhneUrlaubNetto * SOLO_BUFFER_FACTOR;
  const sollInklUrlaubNetto = kostendeckungInklUrlaubNetto * SOLO_GAIN_FACTOR;

  return {
    monatsUmsatzNetto,
    azubiKostenMonat,
    virtuelleGemeinkosten,
    virtuellerMaterialaufwand,
    unternehmerAufwand,
    gesamtKostenSolo,
    virtuellerGeraeteaufwand,
    virtuellerGemeinkostenfaktor,
    prodStundenlohnChef,
    prodLohnkostenfaktor,
    materialkostenfaktor,
    geraetekostenfaktor,
    ueberschussNetto,
    ueberschussProzent,
    inhaberErtragSoll,
    rentabilitaetSoll,
    kostendeckungOhneUrlaubNetto,
    kostendeckungOhneUrlaubBrutto: kostendeckungOhneUrlaubNetto * vat,
    sollOhneUrlaubNetto,
    sollOhneUrlaubBrutto: sollOhneUrlaubNetto * vat,
    kostendeckungInklUrlaubNetto,
    kostendeckungInklUrlaubBrutto: kostendeckungInklUrlaubNetto * vat,
    sollInklUrlaubNetto,
    sollInklUrlaubBrutto: sollInklUrlaubNetto * vat,
  };
}

export const useSoloSimulationStore = create<SoloSimulationState>((set, get) => ({
  snapshot: null,
  isHydrated: false,
  knobsSeeded: false,
  ...defaultSoloKnobs(),

  hydrateFromLiveAkte: ({ mandantUid, year, snapshot }) => {
    const prev = get();
    const nextSnap: SoloSimulationSnapshot = {
      ...snapshot,
      sourceMandantUid: mandantUid,
      sourceYear: year,
      capturedAt: new Date().toISOString(),
      mwstFaktor: snapshot.mwstFaktor ?? SOLO_DEFAULT_VAT,
      gewerbesteuerInBetrieblichenSteuern:
        snapshot.gewerbesteuerInBetrieblichenSteuern ?? false,
      basePersonalkostenIst: snapshot.basePersonalkostenIst ?? 0,
      baseGeraetekostenMonat: snapshot.baseGeraetekostenMonat ?? 0,
      baseGeraetekostenfaktor: snapshot.baseGeraetekostenfaktor ?? 0,
      fixCostLines: Array.isArray(snapshot.fixCostLines) ? snapshot.fixCostLines : [],
      variableCostLines: Array.isArray(snapshot.variableCostLines)
        ? snapshot.variableCostLines
        : [],
      defaultMonatsUmsatzBrutto:
        snapshot.defaultMonatsUmsatzBrutto ?? SOLO_DEFAULT_UMSATZ_BRUTTO,
      defaultUnternehmerlohn: snapshot.defaultUnternehmerlohn ?? 2_500,
      defaultWochenstunden:
        snapshot.defaultWochenstunden ?? SOLO_DEFAULT_WOCHENSTUNDEN,
      lohnfortzahlungPercent: snapshot.lohnfortzahlungPercent ?? 20,
      verlustPercent: snapshot.verlustPercent ?? 15,
      warenwirtschaftAufschlagVk: snapshot.warenwirtschaftAufschlagVk ?? 1.3,
      liquiditaetPercent: snapshot.liquiditaetPercent ?? 12.5,
    };

    // Prefill-Knobs nur beim ersten Hydrate, damit User-Eingaben nicht überschrieben werden
    if (!prev.knobsSeeded) {
      set({
        snapshot: nextSnap,
        isHydrated: true,
        knobsSeeded: true,
        monatsUmsatzBrutto: nextSnap.defaultMonatsUmsatzBrutto,
        unternehmerLohnZiel: nextSnap.defaultUnternehmerlohn,
        ekSteuersatzPercent: nextSnap.baseSteuersatzPercent,
        wochenstunden: nextSnap.defaultWochenstunden,
        gemeinkostenAnpassungNetto: 0,
        azubiAktiv: false,
        azubiVerguetungBrutto: 0,
        azubiWochenstunden: 0,
        geraeteAktiv: true,
      });
    } else {
      set({ snapshot: nextSnap, isHydrated: true });
    }
  },

  resetKnobs: () => {
    const snap = get().snapshot;
    if (!snap) {
      set({ ...defaultSoloKnobs() });
      return;
    }
    set({
      monatsUmsatzBrutto: snap.defaultMonatsUmsatzBrutto,
      unternehmerLohnZiel: snap.defaultUnternehmerlohn,
      ekSteuersatzPercent: snap.baseSteuersatzPercent,
      wochenstunden: snap.defaultWochenstunden,
      gemeinkostenAnpassungNetto: 0,
      azubiAktiv: false,
      azubiVerguetungBrutto: 0,
      azubiWochenstunden: 0,
      geraeteAktiv: true,
    });
  },

  clearSandbox: () =>
    set({
      snapshot: null,
      isHydrated: false,
      knobsSeeded: false,
      ...defaultSoloKnobs(),
    }),

  setMonatsUmsatzBrutto: (v) => set({ monatsUmsatzBrutto: Number.isFinite(v) ? v : 0 }),
  setUnternehmerLohnZiel: (v) => set({ unternehmerLohnZiel: Number.isFinite(v) ? v : 0 }),
  setWochenstunden: (v) => set({ wochenstunden: Number.isFinite(v) ? v : 0 }),
  setGemeinkostenAnpassungNetto: (v) =>
    set({ gemeinkostenAnpassungNetto: Number.isFinite(v) ? v : 0 }),
  setEkSteuersatzPercent: (v) => set({ ekSteuersatzPercent: Number.isFinite(v) ? v : 0 }),
  setAzubiAktiv: (v) => set({ azubiAktiv: !!v }),
  setAzubiVerguetungBrutto: (v) =>
    set({ azubiVerguetungBrutto: Number.isFinite(v) ? v : 0 }),
  setAzubiWochenstunden: (v) => set({ azubiWochenstunden: Number.isFinite(v) ? v : 0 }),
  setGeraeteAktiv: (v) => set({ geraeteAktiv: !!v }),

  getDerived: () => {
    const state = get();
    const knobs: SoloSimulationKnobs = {
      monatsUmsatzBrutto: state.monatsUmsatzBrutto,
      unternehmerLohnZiel: state.unternehmerLohnZiel,
      wochenstunden: state.wochenstunden,
      gemeinkostenAnpassungNetto: state.gemeinkostenAnpassungNetto,
      ekSteuersatzPercent: state.ekSteuersatzPercent,
      azubiAktiv: state.azubiAktiv,
      azubiVerguetungBrutto: state.azubiVerguetungBrutto,
      azubiWochenstunden: state.azubiWochenstunden,
      geraeteAktiv: state.geraeteAktiv,
    };
    return computeSoloDerived(state.snapshot, knobs);
  },
}));
